import { HockeyPenaltyTypes } from '../localization/hockey-penalty-types';

export const MODEL_MAPPING_FIELDS_KEY = '_mapping_fields_';
export const MODEL_RELATION_KEY = '_relation_';
export const MODEL_TO_FRONT_KEY = '_to_front_';
export const MODEL_TO_BACK_KEY = '_to_back_';

type ParserInterface = (dto: any) => any;

interface ModelTypeInterface {
  new(data?: any);
}

export interface ModelInterface {
  toFront(dto: any): any;
  toBack(dto: any): any;
}

export abstract class BaseModel {
  constructor(data?: {[key: string]: any}) {
    if (data) {
      for (const key of Object.keys(data)) {
        this[key] = data[key];
      }
    }
  }

  static toFront(dto: any): any {
    return undefined;
  }

  static toBack(dto: any): any {
    return undefined;
  }
}

export function enumField(enumClass) {
  return {
    toFront: (value) => {
      if (value === null || value === undefined) {
        return value;
      }
      if (isNaN(+value)) {
        return enumClass[value];
      } else {
        return value;
      }
    },
    toBack: (value) => {
      if (value === null || value === undefined) {
        return value;
      }
      if (isNaN(+value)) {
        return value;
      } else {
        return enumClass[value];
      }
    }
  };
}

export function penaltyTypeField() {
  return {
    toFront: (data) => {
      if (!data) {
        return null;
      }
      if (typeof data === 'object') {
        return data;
      }
      return HockeyPenaltyTypes.find(item => item.id === data);
    },
    toBack: (data) => {
      if (!data) {
        return null;
      }
      if (typeof data === 'object') {
        return data.id;
      }
      return data;
    }
  };
}

export function listField(modelClass) {
  return {
    toFront: (value) => {
      if (!Array.isArray(value)) {
        return [];
      }
      return value.map(item => modelClass.toFront(item));
    },
    toBack: (value) => {
      if (!Array.isArray(value)) {
        return [];
      }
      return value.map(item => modelClass.toBack(item));
    }
  };
}

export class DateField extends BaseModel {
  static toFront(date: any): any {
    if (date) {
      return new Date(date);
    } else {
      return undefined;
    }
  }
  static toBack(date: Date): any {
    if (date) {
      const dateUtc = new Date(Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds())
      );
      return dateUtc.toISOString().slice(0, 10);
    } else {
      return undefined;
    }
  }
}

export class DateTimeField extends BaseModel {
  static toFront(date: any): any {
    if (date) {
      return new Date(date);
    } else {
      return undefined;
    }
  }
  static toBack(date: Date): any {
    if (date) {
      const dateUtc = new Date(Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds())
      );
      return date.toISOString();
    } else {
      return undefined;
    }
  }
}

export function ModelInstance(
  config: {
    mappingFields?: { [key: string]: string },
    relation?: { [key: string]: BaseModel }
  }
) {
  return (target: ModelTypeInterface) => {
    if (config) {
      if (config.mappingFields) {
        target[MODEL_MAPPING_FIELDS_KEY] = config.mappingFields;
      }
      if (config.relation) {
        target[MODEL_RELATION_KEY] = config.relation;
      }
      target[MODEL_TO_FRONT_KEY] = (dto: any) => {
        return toFront(target, dto);
      };
      target[MODEL_TO_BACK_KEY] = (dto: any) => {
        return toBack(target, dto);
      };
    }
  };
}

export const ToFrontHook = (target: ModelTypeInterface, key: string | symbol, descriptor: TypedPropertyDescriptor<Function>) => {
  return {
    value: (...args: any[]) => {
      const parseMethod = target[MODEL_TO_FRONT_KEY] as ParserInterface;
      if (parseMethod) {
        return parseMethod(args[0]);
      }
      return undefined;
    }
  };
};

export const ToBackHook = (target: ModelTypeInterface, key: string | symbol, descriptor: TypedPropertyDescriptor<Function>) => {
  return {
    value: (...args: any[]) => {
      const parseMethod = target[MODEL_TO_BACK_KEY] as ParserInterface;
      if (parseMethod) {
        return parseMethod(args[0]);
      }
      return undefined;
    }
  };
};

function extractValue(key, data) {
  if (!data) {
    return undefined;
  }

  const keyParts = key.split('.');
  if (keyParts.length === 1) {
    if (key in data) {
      return data[key];
    } else {
      return undefined;
    }
  }

  for (const k of keyParts) {
    if (data && k in data) {
      data = data[k];
    } else {
      return undefined;
    }
  }

  return data;
}

function toFront(modelType: ModelTypeInterface, data: any): any {
  // Check if recursion is required
  if (Array.isArray(data)) {
    return data.map(item => {
      return toFront(modelType, item);
    });
  }

  const instance = new modelType();

  // Mapping fields
  const mappingFields = modelType[MODEL_MAPPING_FIELDS_KEY] as string[];
  const relation = modelType[MODEL_RELATION_KEY] as { [key: string]: ModelInterface };
  if (mappingFields) {
    for (const backField of Object.keys(mappingFields)) {
      const frontField = mappingFields[backField];
      const value = extractValue(backField, data);
      if (relation && relation.hasOwnProperty(frontField)) {
        instance[frontField] = relation[frontField].toFront(value);
      } else {
        instance[frontField] = value;
      }
    }
  }

  return instance;
}

function toBack(modelType: ModelTypeInterface, instance: any): any {
  if (!instance) {
    return instance;
  }

  // Check if recursion is required
  if (Array.isArray(instance)) {
    return instance.map(item => {
      return toBack(modelType, item);
    });
  }

  const data = {};
  const mappingFields = modelType[MODEL_MAPPING_FIELDS_KEY] as string[];
  const relation = modelType[MODEL_RELATION_KEY] as { [key: string]: ModelInterface };
  if (mappingFields) {
    for (const backField of Object.keys(mappingFields)) {
      const frontField = mappingFields[backField];
      if (relation && relation.hasOwnProperty(frontField) && instance[frontField] !== undefined) {
        data[backField] = relation[frontField].toBack(instance[frontField]);
      } else {
        data[backField] = instance[frontField];
      }
    }
  }

  return data;
}
